import React from "react"
import { Field } from "formik"
import { FieldContainer } from "./FieldContainer"
import { Fields } from "./formBuilder-types"
import { useFieldDictionary } from "hooks/useFieldDictionary"

const PLACEHOLDER_MESSAGE: { [key: string]: string } = {
  full_name: "Tony Smith",
  zip: "96000",
  phone: "(888) 888-888",
}

export const FieldsParser = ({
  custom_place_holder,
  form_field,
  form_field_type,
  hidden_field_value,
  label,
  options,
  required,
}: Fields) => {
  const hasOptionsFilled = !!options
  const formFieldCodename = form_field
    ? useFieldDictionary(form_field)
    : "custom"

  const isCustomFormField = formFieldCodename === "custom"

  const fieldLabel = label!
  const customFieldLabel = fieldLabel?.replace(/\s+/g, "")!

  const htmlFor = isCustomFormField ? customFieldLabel : formFieldCodename
  const isRequired = required

  let emailDatalayerValue = ""

  const handleEmailDatalayer = (e: { target: HTMLInputElement }) => {
    emailDatalayerValue = e.target.value
    if ((window as any).dataLayer) {
      ;(window as any).dataLayer.push({ emailAddress: emailDatalayerValue })
    }
  }

  switch (form_field_type) {
    case "Date":
      return (
        <FieldContainer
          htmlFor={htmlFor}
          label={fieldLabel}
          isFieldRequired={isRequired}
          className="fr-form-builder__field"
        >
          <Field name={htmlFor} id={htmlFor} type="date" required />
        </FieldContainer>
      )

    case "Email":
      return (
        <FieldContainer
          htmlFor={htmlFor}
          label={fieldLabel}
          isFieldRequired={isRequired}
          className="fr-form-builder__field"
        >
          <Field
            id={htmlFor}
            name={htmlFor}
            type="email"
            placeholder={custom_place_holder || "name@email.com"}
            onBlur={handleEmailDatalayer}
          />
        </FieldContainer>
      )

    case "Long Text":
      return (
        <FieldContainer
          htmlFor={htmlFor}
          label={fieldLabel}
          isFieldRequired={isRequired}
          className="fr-form-builder__field fr-form-builder__field--text-area"
        >
          <Field
            name={htmlFor}
            id={htmlFor}
            as="textarea"
            placeholder={custom_place_holder || "Message"}
          />
        </FieldContainer>
      )

    case "Dropdown Select":
      if (!hasOptionsFilled) {
        console.error("Please, fill in the options for the dropdown")
      }

      const optsArr = hasOptionsFilled ? options?.split(",") : undefined

      return (
        <FieldContainer
          htmlFor={htmlFor}
          label={fieldLabel}
          isFieldRequired={isRequired}
          className="fr-form-builder__field"
        >
          <Field name={htmlFor} as="select">
            {hasOptionsFilled &&
              optsArr?.map((option, index) => {
                return <option key={option + index}>{option}</option>
              })}

            {!hasOptionsFilled && <option>No option added in strapi</option>}
          </Field>
        </FieldContainer>
      )

    case "Radio":
      if (!hasOptionsFilled) {
        console.error("Please, fill in the options for the radio")
      }

      const optionsArr = hasOptionsFilled ? options?.split(",") : undefined

      return (
        <FieldContainer
          role="group"
          aria-labelledby={`${htmlFor}-group`}
          htmlFor={htmlFor}
          label={fieldLabel}
          isFieldRequired={isRequired}
        >
          <div className="fr-form-builder__field--radio-group">
            {hasOptionsFilled &&
              optionsArr?.map((option, i) => {
                return (
                  <label
                    key={`${form_field_type}-${htmlFor}-${i}`}
                    htmlFor={htmlFor + i}
                    className="fr-form-builder__radio-item"
                  >
                    <Field
                      type="radio"
                      name={htmlFor}
                      id={htmlFor + i}
                      value={option}
                    />
                    {option}
                  </label>
                )
              })}

            {!hasOptionsFilled && (
              <label htmlFor={htmlFor} className="fr-form-builder__radio-item">
                <Field type="radio" name={htmlFor} value="N/A" />
                N/A
              </label>
            )}
          </div>
        </FieldContainer>
      )

    case "Hidden":
      return <Field name={htmlFor} type="hidden" value={hidden_field_value} />
    default:
      const fieldPlaceholder = PLACEHOLDER_MESSAGE[htmlFor] ?? ""
      return (
        <FieldContainer
          htmlFor={htmlFor}
          label={fieldLabel}
          isFieldRequired={isRequired}
          className="fr-form-builder__field"
        >
          <Field
            name={htmlFor}
            id={htmlFor}
            type="text"
            placeholder={custom_place_holder || fieldPlaceholder}
          />
        </FieldContainer>
      )
  }
}
